import {
  createAction,
  createGetter,
  createMutation,
  isDefined,
  ListStore,
  on,
  StoreFeature,
  Uuid,
  uuid
} from '@softline/core';
import { LoadingMessageBarConfig, MessageBarAction, MessageBarConfig } from './data/message-bar.config';
import { SOFTLINE_CONFIG_MESSAGE_BAR_DURATION } from './message-bar.shared';
import { Signal } from '@angular/core';
import { MessageBarService } from './message-bar.service';

export interface MessageBarFactoryParams {
  message?: string | Signal<string>;
  title?: string | Signal<string>;
  actions?: MessageBarAction[];
  duration?: number;
  onDismiss?(): void;
}

export interface StoreLoadingMessageBarConfig {
  message?: string | Signal<string>
  onDismiss?(): void;
}

export interface StoreProgressMessageBarConfig {
  title?: string | Signal<string>;
  message?: string | Signal<string>;
  progress?: number;
  onDismiss?(): void;
}

export interface State extends ListStore.State<MessageBarConfig & {id: Uuid}> {}

const listStore = ListStore.create<MessageBarConfig & {id: Uuid}>();
/**
 * @deprecated: Use messageFn instead
 */
export const mutations = {
  ...listStore.mutations,
};

/**
 * @deprecated: Use messageFn instead
 */
export const getters = {
  ...listStore.getters,
};
/**
 * @deprecated: Use messageFn instead
 */
export const actions = {
  open: createAction<State, MessageBarConfig, string>('open'),
  close: createAction<State>('close'),
  closeContainer: createAction<State, string>('closeContainer'),
  closeAll: createAction<State>('closeAll'),

  success: createAction<State, string | MessageBarFactoryParams, string>(
    'success'
  ),
  warning: createAction<State, string | MessageBarFactoryParams, string>(
    'warning'
  ),
  error: createAction<State, string | MessageBarFactoryParams, string>('error'),
  info: createAction<State, string | MessageBarFactoryParams, string>('info'),
  loading: createAction<
    State,
    string | StoreLoadingMessageBarConfig | undefined,
    string
  >('loading'),
};

export const feature: StoreFeature<State> = {
  initialState: {
    items: [],
  },
  mutations: [
    ...listStore.feature.mutations,
  ],
  getters: [
    ...listStore.feature.getters,
  ],
  actions: [
    // tslint:disable-next-line:no-shadowed-variable
    on(actions.open, ({ store, state, featureName, params, injector }) => {
      const p = { ...params, id: uuid() };
      store.commit(featureName, mutations.add, p);
      if ((p.duration ?? 0) < Number.POSITIVE_INFINITY) {
        setTimeout(() => {
          if (store.get(featureName, getters.items).indexOf(p) > -1)
            store.dispatch(featureName, actions.close, p.id);
        }, params.duration ?? injector.get(SOFTLINE_CONFIG_MESSAGE_BAR_DURATION));
      }
      return p.id;
    }),
    on(actions.close, ({ store, state, featureName, params }) => {
      const el = state.items.find((o) => o.id === params);
      if (!isDefined(el))
        throw new Error(`MessageBar with id '${params}' fot found in store`);
      store.commit(featureName, mutations.remove, el);
    }),
    on(actions.closeAll, ({ store, state, featureName, params }) => {
      const el = state.items.find((o) => o.id === params);
      store.commit(featureName, mutations.clear);
    }),
    on(actions.success, async ({ store, params, injector, featureName }) => {

      const messageBarService = injector.get(MessageBarService);
      if(typeof params === 'string')
        return messageBarService.success(params as string);
      else
        return messageBarService.success(params.message ?? '', params.title ?? null, { actions: params.actions, duration: params.duration, onDismiss: params.onDismiss });
    }),
    on(actions.warning, async ({ store, params, injector, featureName }) => {

      const messageBarService = injector.get(MessageBarService);
      if(typeof params === 'string')
        return messageBarService.warning(params as string);
      else
        return messageBarService.warning(params.message ?? '', params.title ?? null, { actions: params.actions, duration: params.duration, onDismiss: params.onDismiss });
    }),
    on(actions.error, async ({ store, params, injector, featureName }) => {
      const messageBarService = injector.get(MessageBarService);
      if(typeof params === 'string')
        return messageBarService.error(params as string);
      else
        return messageBarService.error(params.message ?? '', params.title ?? null, { actions: params.actions, duration: params.duration, onDismiss: params.onDismiss });
    }),
    on(actions.info, async ({ store, params, injector, featureName }) => {

      const messageBarService = injector.get(MessageBarService);
      if(typeof params === 'string')
        return messageBarService.info(params as string);
      else
        return messageBarService.info(params.message ?? '', params.title ?? null, { actions: params.actions, duration: params.duration, onDismiss: params.onDismiss });
    }),
    on(actions.loading, async ({ store, params, injector, featureName }) => {
      if (typeof params === 'string') params = { message: params };

      const config: LoadingMessageBarConfig = {
        type: 'loading',
        title: 'Lade...',
        message: params?.message,
        icon: 'fa-regular fa-spinner fa-spin',
        duration: Number.POSITIVE_INFINITY,
        dismiss: !!params?.onDismiss,
        onDismiss: params?.onDismiss,
      };

      return await store.dispatch(featureName, actions.open, config, injector);
    }),
  ],
};

function messageFactory(
  type: 'success' | 'warning' | 'error' | 'info' | 'loading' | 'saving' | 'progress',
  params: MessageBarFactoryParams | string,
  title: string,
  icon: string
): MessageBarConfig {
  if (typeof params === 'string')
    params = { message: params };
  if(type === 'error' && !params.duration)
    params = { ...params, duration: Number.POSITIVE_INFINITY };
  return {
    type,
    title: params?.title ?? title,
    message: params.message,
    icon,
    duration: params?.duration,
    actions: params?.actions,
    dismiss: true,
    onDismiss: params?.onDismiss,
  };
}
